import { useOutletContext, Link } from 'react-router';
import { CreditCard, Repeat, Receipt } from 'lucide-react';
import { Card, Button, StatusBadge } from '@sighthost/ui';
import type { StatusTone } from '@sighthost/ui';
import type { Service } from '@/domains/hosting';
import { useHostingSubscriptions } from '@/domains/billing';
import type { HostingSubscription, HostingSubscriptionStatus } from '@/domains/billing';

function statusTone(status: HostingSubscriptionStatus): StatusTone {
  switch (status) {
    case 'active': return 'active';
    case 'pending': return 'pending';
    case 'at_risk': return 'overdue';
    case 'cancelled': return 'draft';
  }
}

function statusLabel(status: HostingSubscriptionStatus): string {
  switch (status) {
    case 'active': return 'Active';
    case 'pending': return 'Setting up';
    case 'at_risk': return 'Payment failed';
    case 'cancelled': return 'Cancelled';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatZAR(n: number): string {
  return `R${n.toFixed(2)}`;
}

/**
 * ServiceBillingTab — per-service billing summary.
 *
 * Read-only by design — actions live on /app/billing/auto-pay so there's one
 * place to act. This tab surfaces enough that the customer can see at a
 * glance what billing model their hosting is on, and click through to
 * either manage auto-pay or view invoices.
 *
 * PF-Q3 BRD §7.1 deferred-item.
 */
export default function ServiceBillingTab() {
  const service = useOutletContext<Service>();
  const { subscriptions, isLoading } = useHostingSubscriptions();

  const sub = subscriptions.find(
    (s) => s.customerLinkId === service.id && s.status !== 'cancelled'
  ) ?? null;

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-5">
        <div className="flex items-start gap-3 border-b border-border pb-3">
          <Repeat className="mt-0.5 size-5 text-primary" aria-hidden />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-ink">Billing model</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {sub
                ? 'This hosting account is on PayFast auto-pay — card charged automatically each month.'
                : 'This hosting account is billed monthly via invoice. Pay by EFT or one-click PayFast.'}
            </p>
          </div>
          {sub && (
            <StatusBadge tone={statusTone(sub.status)}>
              {statusLabel(sub.status)}
            </StatusBadge>
          )}
        </div>

        {!isLoading && sub && (
          <SubscriptionDetails subscription={sub} />
        )}

        {!isLoading && !sub && (
          <div className="pt-4 text-sm text-muted-foreground">
            Want auto-pay? Set it up in one click — your card is held by PayFast, not by us.
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild size="sm" variant={sub ? 'outline' : 'primary'}>
            <Link to="/app/billing/auto-pay">
              <CreditCard className="size-4" aria-hidden />
              {sub ? 'Manage auto-pay' : 'Set up auto-pay'}
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/app/billing">
              <Receipt className="size-4" aria-hidden />
              View invoices
            </Link>
          </Button>
        </div>
      </Card>

      <Card interactive={false} className="p-5">
        <h3 className="mb-2 text-sm font-semibold text-ink">
          Invoices for this service
        </h3>
        <p className="text-sm text-muted-foreground">
          All invoices for{' '}
          <strong className="tabular-nums text-ink">{service.primaryDomain}</strong>{' '}
          appear in the main billing list. A per-service filtered view will land in a future
          release.
        </p>
      </Card>
    </div>
  );
}

function SubscriptionDetails({ subscription }: { subscription: HostingSubscription }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 pt-4 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Next charge
        </dt>
        <dd className="text-foreground">
          {formatDate(subscription.nextBillingDate)} · {formatZAR(subscription.recurringAmount)}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Last charge
        </dt>
        <dd className="text-foreground">
          {subscription.lastCycleAt ? formatDate(subscription.lastCycleAt) : '—'}
        </dd>
      </div>
      {subscription.failedCycleAt && subscription.status === 'at_risk' && (
        <div className="sm:col-span-2 mt-1 rounded border border-warning/40 bg-warning/10 p-3 text-sm text-warning-foreground">
          Last payment failed on {formatDate(subscription.failedCycleAt)}. Update your card
          via PayFast — we'll retry automatically. If the card isn't updated within 7 days,
          auto-pay reverts to monthly invoices.
        </div>
      )}
    </dl>
  );
}
